import { useState, useEffect, useCallback } from 'react';
import { chatsApi } from '../../../api/chats';

/**
 * Хук режима выбора сообщений: выделение, копирование, удаление, пересылка.
 *
 * @param {Object} params
 * @param {string} params.id — ID чата (используется для сброса при смене чата)
 * @param {Array} params.messages — массив сообщений из useChatMessages
 * @param {Function} params.setMessages — setter сообщений
 * @param {string} [params.currentProfileId] — ID профиля текущего пользователя
 * @param {Function} params.setError — setter ошибок
 * @returns {Object} состояние выбора и обработчики
 */
export function useMessageSelection({ id, messages, setMessages, currentProfileId, setError, onForwarded }) {
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
    const [showForwardModal, setShowForwardModal] = useState(false);
    const [singleForwardMessage, setSingleForwardMessage] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Сброс при смене чата
    useEffect(() => {
        setSelectionMode(false);
        setSelectedMessageIds(new Set());
        setSingleForwardMessage(null);
        setShowForwardModal(false);
        setActionLoading(false);
    }, [id]);

    const enterSelectionMode = useCallback((message) => {
        setSelectionMode(true);
        if (message?.id) {
            setSelectedMessageIds(new Set([message.id]));
        }
    }, []);

    const exitSelectionMode = useCallback(() => {
        setSelectionMode(false);
        setSelectedMessageIds(new Set());
    }, []);

    // Выход из режима выбора по Escape
    useEffect(() => {
        if (!selectionMode) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                exitSelectionMode();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectionMode, exitSelectionMode]);

    // Автоматический выход из режима выбора, если сняты все выделения
    useEffect(() => {
        if (selectionMode && selectedMessageIds.size === 0) {
            exitSelectionMode();
        }
    }, [selectionMode, selectedMessageIds, exitSelectionMode]);

    const allSelected = messages.length > 0 && selectedMessageIds.size === messages.length;

    const toggleSelectAll = useCallback(() => {
        if (allSelected) {
            setSelectedMessageIds(new Set());
        } else {
            setSelectedMessageIds(new Set(messages.map(m => m.id)));
        }
    }, [allSelected, messages]);

    const toggleMessageSelection = useCallback((message) => {
        if (!message?.id) return;
        setSelectedMessageIds(prev => {
            const next = new Set(prev);
            if (next.has(message.id)) {
                next.delete(message.id);
            } else {
                next.add(message.id);
            }
            return next;
        });
    }, []);

    const handleDeleteSelected = async () => {
        if (selectedMessageIds.size === 0) return;

        setActionLoading(true);
        try {
            const idsToDelete = Array.from(selectedMessageIds);
            await Promise.all(idsToDelete.map(msgId => chatsApi.deleteMessage(msgId)));

            setMessages(prev => prev.filter(m => !selectedMessageIds.has(m.id)));
            exitSelectionMode();
        } catch (err) {
            console.error('[useMessageSelection] Failed to delete messages:', err);
            setError(err.message || 'Ошибка при удалении сообщений');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCopySelected = useCallback(async () => {
        const messagesToCopy = messages
            .filter(m => selectedMessageIds.has(m.id))
            .map(m => m.content || m.text || '')
            .filter(Boolean);
        if (messagesToCopy.length === 0) return;
        const text = messagesToCopy.join('\n');
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try { document.execCommand('copy'); } catch {}
            document.body.removeChild(textarea);
        }
        exitSelectionMode();
    }, [messages, selectedMessageIds, exitSelectionMode]);

    const handleForwardSelected = async (targetChatId, comment = null) => {
        if ((selectedMessageIds.size === 0 && !singleForwardMessage) || !targetChatId) return;

        setActionLoading(true);
        try {
            const messagesToForward = singleForwardMessage
                ? [singleForwardMessage]
                : messages.filter(m => selectedMessageIds.has(m.id));

            // Use dedicated forward endpoint — server copies content, attachments,
            // and poll data, sets forwardedFrom* fields.
            await Promise.all(
                messagesToForward.map(msg =>
                    chatsApi.forwardMessage(msg.id, targetChatId, comment)
                )
            );

            setShowForwardModal(false);
            setSingleForwardMessage(null);
            exitSelectionMode();

            // Переходим в чат, куда переслали сообщение
            if (typeof onForwarded === 'function') {
                onForwarded(targetChatId);
            }
        } catch (err) {
            console.error('[useMessageSelection] Failed to forward messages:', err);
            setError(err.response?.data?.message || err.message || 'Ошибка при пересылке сообщений');
        } finally {
            setActionLoading(false);
        }
    };

    const handleForwardSingle = useCallback((message) => {
        setSingleForwardMessage(message);
        setShowForwardModal(true);
    }, []);

    return {
        selectionMode,
        setSelectionMode,
        selectedMessageIds,
        showForwardModal,
        setShowForwardModal,
        singleForwardMessage,
        setSingleForwardMessage,
        actionLoading,
        allSelected,
        enterSelectionMode,
        exitSelectionMode,
        toggleSelectAll,
        toggleMessageSelection,
        handleDeleteSelected,
        handleCopySelected,
        handleForwardSelected,
        handleForwardSingle,
    };
}
